import { useTranslation } from 'react-i18next';
import { UsersFilters } from '../components/admin/UsersFilters';
import { GroupsFilters } from '../components/admin/GroupsFilters';
import { AdminUsersPageHeader } from '../components/admin/AdminUsersPageHeader';
import { AdminUsersResults } from '../components/admin/AdminUsersResults';
import { UserModal } from '../components/admin/UserModal';
import { GroupModal } from '../components/admin/GroupModal';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import { useAdminUsersController } from '../hooks/useAdminUsersController';
import { CircleNotchIcon } from '@phosphor-icons/react';

export const AdminUsersPage = () => {
  const { t } = useTranslation();
  const controller = useAdminUsersController();
  const {
    selectedCompanyId,
    canManageGroups,
    activeTab,
    currentPage,
    search,
    setSearch,
    roleFilter,
    groupSearch,
    setGroupSearch,
    userModalRef,
    groupModalRef,
    usersData,
    isUsersLoading,
    usersError,
    groupsData,
    isGroupsLoading,
    groupsError,
    isRemoveModalOpen,
    isRemovePending,
    setCurrentPage,
    handleUserAction,
    handleRemoveUser,
    closeRemoveModal,
    handleRoleFilterChange,
    handleTabChange,
    handleGroupAction,
    handleBulkDeleteGroups,
    handleBulkResendUsers,
    handleBulkRemoveUsers,
  } = controller;

  if (!selectedCompanyId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <p className="text-gray-500">{t('common.error')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="left-80 flex flex-col overflow-hidden">
      <div className="flex-1 h-full flex flex-col max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-lg">
          <AdminUsersPageHeader
            activeTab={activeTab}
            canManageGroups={canManageGroups}
            onTabChange={handleTabChange}
            onInviteUser={() => userModalRef.current?.openInvite()}
            onCreateGroup={() => groupModalRef.current?.openCreate()}
          />

          {activeTab === 'users' && (
            <UsersFilters
              search={search}
              onSearchChange={setSearch}
              roleFilter={roleFilter}
              onRoleFilterChange={handleRoleFilterChange}
            />
          )}

          {activeTab === 'groups' && (
            <GroupsFilters search={groupSearch} onSearchChange={setGroupSearch} />
          )}

          {(isGroupsLoading || isUsersLoading) && (
            <div className="flex justify-center items-center py-12">
              <CircleNotchIcon size={32} className="animate-spin text-primary" />
            </div>
          )}

          {(groupsError || usersError) && (
            <div className="p-6 text-center text-red-600">{t('common.error')}</div>
          )}
        </div>

        <AdminUsersResults
          activeTab={activeTab}
          canManage={canManageGroups}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          usersData={usersData}
          isUsersLoading={isUsersLoading}
          usersError={usersError}
          onUserAction={handleUserAction}
          onBulkResendUsers={handleBulkResendUsers}
          onBulkRemoveUsers={handleBulkRemoveUsers}
          groupsData={groupsData}
          isGroupsLoading={isGroupsLoading}
          groupsError={groupsError}
          onGroupAction={handleGroupAction}
          onBulkDeleteGroups={canManageGroups ? handleBulkDeleteGroups : undefined}
        />
      </div>

      <UserModal ref={userModalRef} companyId={selectedCompanyId} />

      <GroupModal ref={groupModalRef} companyId={selectedCompanyId} />

      <ConfirmationModal
        isOpen={isRemoveModalOpen}
        onClose={closeRemoveModal}
        onConfirm={handleRemoveUser}
        title={t('admin.users.remove_confirmation.title')}
        message={t('admin.users.remove_confirmation.message')}
        confirmText={t('admin.users.remove_confirmation.confirm')}
        cancelText={t('admin.users.remove_confirmation.cancel')}
        isLoading={isRemovePending}
        isDanger={true}
      />
    </div>
  );
};
